import type { QueryClient } from "@tanstack/react-query";
import {
  mergeBrandingDraft,
  resolveBackgroundImageUrl,
} from "@/components/branding/brandingUtils";
import {
  brandingKeys,
  getResolvedBranding,
} from "@/services/api/agentic-v2/branding";
import { warmSharedImageBlobUrl } from "@/services/api/sharedImageBlobCache";
import type {
  BrandingBackground,
  EffectiveBranding,
} from "@/services/api/agentic-v2/types";
import {
  apiImageUrlNeedsSessionFetch,
  toProxyUrl,
} from "@/utils/imageUrl";

const LEGACY_STICKY_LOGO_STORAGE_KEY = "ani.os-watermark-logo-url";
const STICKY_BACKGROUND_STORAGE_PREFIX = "ani.os-background-snapshot:";

export type StickyWatermarkScope = {
  userId: string;
  companyId: string;
};

export function buildStickyWatermarkScope(
  user: { id?: string; company_id?: string } | null | undefined,
): StickyWatermarkScope | null {
  if (!user?.id || !user.company_id) return null;
  return { userId: user.id, companyId: user.company_id };
}

function stickyLogoStorageKey(scope: StickyWatermarkScope): string {
  return `${LEGACY_STICKY_LOGO_STORAGE_KEY}:${scope.userId}:${scope.companyId}`;
}

export function removeLegacyStickyWatermarkLogoUrl(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(LEGACY_STICKY_LOGO_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
}

export function readStickyWatermarkLogoUrl(
  scope: StickyWatermarkScope | null | undefined,
): string | null {
  if (!scope || typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(stickyLogoStorageKey(scope));
  } catch {
    return null;
  }
}

export function writeStickyWatermarkLogoUrl(
  scope: StickyWatermarkScope | null | undefined,
  url: string | null,
): void {
  if (!scope || typeof window === "undefined") return;
  try {
    const key = stickyLogoStorageKey(scope);
    if (url) {
      sessionStorage.setItem(key, url);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // ignore storage failures
  }
}

function stickyBackgroundStorageKey(scope: StickyWatermarkScope): string {
  return `${STICKY_BACKGROUND_STORAGE_PREFIX}${scope.userId}:${scope.companyId}`;
}

export function readStickyBackgroundSnapshot(
  scope: StickyWatermarkScope | null | undefined,
): BrandingBackground | null {
  if (!scope || typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(stickyBackgroundStorageKey(scope));
    if (!raw) return null;
    return JSON.parse(raw) as BrandingBackground;
  } catch {
    return null;
  }
}

export function writeStickyBackgroundSnapshot(
  scope: StickyWatermarkScope | null | undefined,
  background: BrandingBackground | null | undefined,
): void {
  if (!scope || typeof window === "undefined") return;
  try {
    const key = stickyBackgroundStorageKey(scope);
    if (background?.mode) {
      sessionStorage.setItem(key, JSON.stringify(background));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // ignore storage failures
  }
}

export function prefetchBrandingBackgroundImages(
  background: BrandingBackground | null | undefined,
): void {
  if (!background || background.mode !== "image") return;

  for (const isDark of [true, false]) {
    const imageUrl = resolveBackgroundImageUrl(background, isDark);
    const proxied = imageUrl ? toProxyUrl(imageUrl) : null;
    if (!proxied || !apiImageUrlNeedsSessionFetch(proxied)) continue;
    warmSharedImageBlobUrl(proxied);
  }
}

export function mergeResolvedWithStickyWatermark(
  resolved: EffectiveBranding,
  scope?: StickyWatermarkScope | null,
): EffectiveBranding {
  writeStickyBackgroundSnapshot(scope, resolved.background);
  prefetchBrandingBackgroundImages(resolved.background);

  const stickyLogoUrl = readStickyWatermarkLogoUrl(scope);
  if (!stickyLogoUrl || !resolved.watermark?.enabled) {
    return resolved;
  }
  if (resolved.watermark.logo_url) {
    return resolved;
  }
  return mergeBrandingDraft(resolved, {
    watermark: { logo_url: stickyLogoUrl },
  });
}

/**
 * Authoritatively sync runtime branding from the API into the resolved query cache.
 * Prefer this over invalidating resolved queries, which can race stale in-flight refetches.
 */
export async function syncResolvedBrandingCache(
  queryClient: QueryClient,
  options?: { asUserId?: string; scope?: StickyWatermarkScope | null },
): Promise<EffectiveBranding> {
  const asUserId = options?.asUserId;
  const resolved = mergeResolvedWithStickyWatermark(
    await getResolvedBranding({ asUserId }),
    options?.scope,
  );
  queryClient.setQueryData(brandingKeys.resolved(asUserId), resolved);
  return resolved;
}

export function patchResolvedWatermarkLogo(
  queryClient: QueryClient,
  logoUrl: string | null,
  options?: { asUserId?: string; scope?: StickyWatermarkScope | null },
): void {
  writeStickyWatermarkLogoUrl(options?.scope, logoUrl);

  queryClient.setQueryData<EffectiveBranding | undefined>(
    brandingKeys.resolved(options?.asUserId),
    (current) => {
      if (!current) return current;
      return mergeBrandingDraft(current, {
        watermark: { logo_url: logoUrl },
      });
    },
  );
}

export function clearStickyWatermarkLogoUrl(
  scope?: StickyWatermarkScope | null,
): void {
  writeStickyWatermarkLogoUrl(scope, null);
}
